import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app'
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth'
import {
  connectFirestoreEmulator,
  Firestore,
  getFirestore,
  initializeFirestore,
} from 'firebase/firestore'
import React, { createContext, useContext, useMemo } from 'react'

import { firebaseConfig } from './firebase'

interface FirebaseServices {
  app: FirebaseApp
  auth: Auth
  firestore: Firestore
}

const FirebaseContext = createContext<FirebaseServices | null>(null)

const USE_EMULATOR = import.meta.env.VITE_USE_EMULATOR === '1'

// Initialise the SDK once at module scope so the very first
// initializeFirestore() call carries our settings (StrictMode double-invokes
// component bodies, which would otherwise race two initialisations).
let sharedServices: FirebaseServices | null = null

function getServices(): FirebaseServices {
  if (sharedServices) return sharedServices
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig)
  const auth = getAuth(app)
  let firestore: Firestore
  try {
    // Against the Firestore emulator the default WebChannel streaming
    // transport can fail to connect (e.g. headless browsers); forcing
    // long-polling makes the listener connect reliably. Only enabled for the
    // emulator path so production transport behaviour is unchanged.
    firestore = initializeFirestore(
      app,
      USE_EMULATOR ? { experimentalForceLongPolling: true } : {},
    )
  } catch {
    firestore = getFirestore(app)
  }
  if (USE_EMULATOR) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
      disableWarnings: true,
    })
    connectFirestoreEmulator(firestore, '127.0.0.1', 8080)
  }
  sharedServices = { app, auth, firestore }
  return sharedServices
}

export function FirebaseProvider({ children }: { children: React.ReactNode }) {
  const services = useMemo<FirebaseServices>(() => getServices(), [])

  return (
    <FirebaseContext.Provider value={services}>
      {children}
    </FirebaseContext.Provider>
  )
}

function useFirebase(): FirebaseServices {
  const ctx = useContext(FirebaseContext)
  if (!ctx) {
    throw new Error('useFirebase must be used within <FirebaseProvider>')
  }
  return ctx
}

export function useFirebaseApp(): FirebaseApp {
  return useFirebase().app
}

export function useAuth(): Auth {
  return useFirebase().auth
}

export function useFirestore(): Firestore {
  return useFirebase().firestore
}
